import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  PaginationComponent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Toaster,
  useToast,
} from '@folio/components';
import { ErrorRow } from '@folio/components/module-elements/ErrorRow';
import { LoadingRow } from '@folio/components/module-elements/LoadingRow';
import { useCurrentPage } from '@folio/hooks/useCurrentPage';
import { useQuery } from '@tanstack/react-query';
import {
  brokerControllerListBrokersQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
  useBrokerControllerDeleteBroker,
  useBrokerControllerUpdateBroker,
} from 'korint-sdk';
import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { InformationField } from '@folio/components/module-elements/InformationCard/InformationField';

const PAGE_SIZE = 10;

export function IntermediaryUsersListModule() {
  const toast = useToast();
  const { t } = useTranslation();

  const { data: user, isLoading: isUserLoading } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const currentPage = useCurrentPage();

  const {
    data: brokers,
    status,
    refetch,
  } = useQuery({
    ...brokerControllerListBrokersQueryOptions(user?.brokerageFirmId!, {
      count: PAGE_SIZE,
      page: currentPage,
    }),
    enabled: !!user?.brokerageFirmId,
  });

  const { mutateAsync: deleteBroker } = useBrokerControllerDeleteBroker({
    mutation: {
      onSuccess: () => {
        toast.toast({
          title: t('intermediaryUsersList.toast.successTitle'),
          description: t('intermediaryUsersList.toast.deleteSuccess'),
        });
        refetch();
      },
      onError: error => {
        toast.toast({
          title: t('intermediaryUsersList.toast.errorTitle'),
          description: error.userError,
        });
      },
    },
  });

  const { mutateAsync: editBroker } = useBrokerControllerUpdateBroker({
    mutation: {
      onSuccess: () => {
        toast.toast({
          title: t('intermediaryUsersList.toast.successTitle'),
          description: t('intermediaryUsersList.toast.editSuccess'),
        });
        refetch();
      },
      onError: error => {
        toast.toast({
          title: t('intermediaryUsersList.toast.errorTitle'),
          description: error.userError,
        });
      },
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('intermediaryUsersList.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('fields.lastName.label')}</TableHead>
              <TableHead>{t('fields.firstName.label')}</TableHead>
              <TableHead>{t('fields.email.label')}</TableHead>
              <TableHead>{t('fields.actions.label')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isUserLoading || status === 'pending' ? (
              // biome-ignore lint/suspicious/noArrayIndexKey: It's all we have here and we don't really care in this case.
              Array.from({ length: PAGE_SIZE }).map((_, idx) => <LoadingRow key={idx} />)
            ) : status === 'error' ? (
              <ErrorRow />
            ) : (
              brokers.data.map(broker => (
                <TableRow key={broker.userId}>
                  <TableCell>
                    <InformationField
                      fieldKey="lastName"
                      label=""
                      updateEntity={async (_, value) => {
                        await editBroker({
                          brokerageFirmId: user?.brokerageFirmId!,
                          brokerUserId: broker.userId,
                          data: {
                            lastName: value as string,
                          },
                        });
                      }}
                      editionMode="INLINE"
                      type="text"
                      value={broker.lastName}
                    />
                  </TableCell>
                  <TableCell>
                    <InformationField
                      fieldKey="firstName"
                      label=""
                      updateEntity={async (_, value) => {
                        await editBroker({
                          brokerageFirmId: user?.brokerageFirmId!,
                          brokerUserId: broker.userId,
                          data: {
                            firstName: value as string,
                          },
                        });
                      }}
                      editionMode="INLINE"
                      type="text"
                      value={broker.firstName}
                    />
                  </TableCell>
                  <TableCell>{broker.email}</TableCell>
                  <TableCell>
                    {user?.brokerageFirmId && (
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() =>
                          deleteBroker({
                            brokerageFirmId: user?.brokerageFirmId!,
                            brokerUserId: broker.userId,
                          })
                        }
                        className="hover:bg-red-50 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    )}
                    <Toaster />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
      <CardFooter>
        <PaginationComponent
          totalData={brokers?.totalCount ?? 0}
          PAGE_SIZE={PAGE_SIZE}
          isLoading={status === 'pending'}
        />
      </CardFooter>
    </Card>
  );
}
